import { Stack } from '@mui/joy';
import { forwardRef, MutableRefObject, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';

import { VendorContactFormField } from 'contacts/elements';
import { FormField } from 'core/components';
import { ApiStatus, InputType, NotificationPosition } from 'core/enums';
import { useFormContext, useNotificationContext } from 'core/states';
import { getFirstErrorMessage } from 'core/utils';
import { ICorporateUserAutocomplete } from 'corporate/types';
import { QuoteEntityType } from 'quotes/enums';
import { IImportVendorQuoteForm } from 'quotes/types';

import { ImportVendorLocationField } from '../../ImportVendorLocationField';
import { useMappedContactHelperTexts } from '../../useMappedContactHelperTexts';
import { getVendorContactLabel, VENDOR_QUOTE_REVISION_MAX_LENGTH } from '../../utils';

export interface IImportContactHandle {
  triggerFormSubmit: () => Promise<IImportVendorQuoteForm | null>;
}

interface IProps {
  disabled?: boolean;
  isContactLocked?: boolean;
  isContactPrefilled?: boolean;
  isVendorLocked?: boolean;
  isVendorMapped?: boolean;
  isVendorMappingRemembered?: boolean;
  showVendorLocation?: boolean;
  showVendorQuoteNumber?: boolean;
  isVendorQuoteNumberLocked?: boolean;
  showVendorQuoteRevision?: boolean;
  suggestedContactIdRef?: MutableRefObject<number | null>;
}

export const ImportContactFormFields = forwardRef<IImportContactHandle, IProps>(
  function ImportContactFormFields(
    {
      disabled = false,
      isContactLocked = false,
      isContactPrefilled = false,
      isVendorLocked = false,
      isVendorMapped = false,
      isVendorMappingRemembered = false,
      showVendorLocation = false,
      showVendorQuoteNumber = false,
      isVendorQuoteNumberLocked = false,
      showVendorQuoteRevision = false,
      suggestedContactIdRef,
    },
    ref
  ) {
    const { t } = useTranslation();
    const { control, errors, handleSubmit } = useFormContext();
    const { showNotification } = useNotificationContext();

    const contactHelperTexts = useMappedContactHelperTexts({ isContactLocked, isVendorMapped });

    useImperativeHandle(ref, () => ({
      triggerFormSubmit: () =>
        new Promise<IImportVendorQuoteForm | null>((resolve) => {
          handleSubmit(
            (data: IImportVendorQuoteForm) => resolve(data),
            (validationErrors) => {
              showNotification({
                type: ApiStatus.ERROR,
                title: t('titleValidationError'),
                message: getFirstErrorMessage(validationErrors) ?? t('serverError'),
                position: NotificationPosition.DRAWER,
              });
              resolve(null);
            }
          )();
        }),
    }));

    return (
      // One column with core/components/Form's 16px field gap; the wrapper matters because the step
      // container is a space-between column that would push two bare siblings to its top and bottom.
      <Stack sx={{ gap: 2 }}>
        {/* Vendor first, then the contact: picking a vendor pre-selects that site's contact. */}
        {(showVendorLocation || isVendorMapped) && (
          <ImportVendorLocationField
            // `disabled` fixes the quote's own fields on a re-import, where the vendor is the one
            // thing the step asks for — so it locks the contact here, never the vendor.
            isContactLocked={disabled}
            isContactPrefilled={isContactPrefilled}
            isVendorLocked={isVendorLocked}
            isVendorMapped={isVendorMapped}
            isVendorMappingRemembered={isVendorMappingRemembered}
            suggestedContactIdRef={suggestedContactIdRef}
          />
        )}

        <VendorContactFormField
          name="contact"
          label={t('labelVendorQuoteContact', { entityType: QuoteEntityType.QUOTE })}
          getLabel={(option: ICorporateUserAutocomplete) => getVendorContactLabel(option)}
          helperTexts={contactHelperTexts}
          required
          disabled={disabled || isContactLocked}
        />

        {/* No core Form above this step to inject `control`/`error`, so they are passed here. */}
        {showVendorQuoteNumber && (
          <FormField
            name="externalNumber"
            type={InputType.TEXT_INPUT}
            inputProps={{
              control,
              error: errors?.externalNumber,
              label: t('labelVendorQuoteNumber'),
              helperTexts: isVendorQuoteNumberLocked ? [] : [t('messageVendorQuoteImportNumber')],
              required: !isVendorQuoteNumberLocked,
              disabled: isVendorQuoteNumberLocked,
            }}
          />
        )}

        {showVendorQuoteRevision && (
          <FormField
            name="revisionNumber"
            type={InputType.TEXT_INPUT}
            inputProps={{
              control,
              error: errors?.revisionNumber,
              label: t('labelVendorQuoteRevision'),
              disabled,
              slotProps: { input: { maxLength: VENDOR_QUOTE_REVISION_MAX_LENGTH } },
            }}
          />
        )}
      </Stack>
    );
  }
);
